import { useState, useEffect, useRef, useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { IconUpload, IconPhoto } from '@tabler/icons-react';
import { applyFilters, computeStatPanel } from '@2bforest/common';
import type { StatPanel, StatDataPoint, StatDataQuery, PanelWithData } from '@2bforest/common';
import {
  listDataPointFields,
  listDataPointsByTheme,
  createPanel,
  updatePanel,
} from '../services/statisticsService';
import { uploadStatPanelIcon } from '../services/storageService';
import StatPanelPreviewCard from './StatPanelPreviewCard';

interface StatPanelModalProps {
  panel: StatPanel | null;
  onSave: (saved: StatPanel) => void;
  onClose: () => void;
}

const DISPLAY_TYPE_LABELS: Record<string, string> = {
  infographic: 'Infographic',
  bar_chart: 'Bar Chart',
  stacked_bar_chart: 'Stacked Bar Chart',
  pie_chart: 'Pie Chart',
  table: 'Table',
};

const LABEL_FIELD_OPTIONS: Array<keyof Omit<StatDataPoint, 'id' | 'value'>> = [
  'theme',
  'variable',
  'dimension',
  'subdimension',
  'category',
  'subcategory1',
  'subcategory2',
  'subcategory3',
  'year',
  'unit',
  'source',
];

const TABLE_COLUMN_OPTIONS: Array<keyof StatDataPoint> = [
  'theme',
  'variable',
  'dimension',
  'subdimension',
  'category',
  'subcategory1',
  'subcategory2',
  'subcategory3',
  'year',
  'unit',
  'source',
];

const FILTER_KEYS: Array<keyof NonNullable<StatDataQuery['filters']>> = [
  'variable',
  'dimension',
  'subdimension',
  'category',
  'subcategory1',
  'subcategory2',
  'subcategory3',
];

const FOOTER_FIELD_OPTIONS: Array<keyof Omit<StatDataPoint, 'id' | 'value'>> = [
  'year',
  'source',
  'unit',
  'variable',
  'dimension',
  'subdimension',
  'category',
];

const ACCEPTED_ICON_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/svg+xml': ['.svg'],
};

// ---------------------------------------------------------------------------
// Icon upload sub-component
// ---------------------------------------------------------------------------

function PanelIconUpload({
  currentUrl,
  onFile,
}: {
  currentUrl: string;
  onFile: (file: File, preview: string) => void;
}) {
  const [preview, setPreview] = useState(currentUrl);
  const [dropError, setDropError] = useState('');

  const onDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      if (rejected.length > 0) {
        const firstError = rejected[0].errors[0];
        if (firstError?.code === 'file-too-large') {
          setDropError('File exceeds 500 KB. Try exporting a smaller PNG or use SVG.');
        } else if (firstError?.code === 'file-invalid-type') {
          setDropError('Unsupported file type. Use JPEG, PNG, or SVG.');
        } else {
          setDropError(firstError?.message ?? 'File rejected.');
        }
        return;
      }
      setDropError('');
      const file = accepted[0];
      if (!file) return;
      const localUrl = URL.createObjectURL(file);
      setPreview(localUrl);
      onFile(file, localUrl);
    },
    [onFile]
  );

  useEffect(() => {
    setPreview(currentUrl);
  }, [currentUrl]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_ICON_TYPES,
    maxFiles: 1,
    maxSize: 500 * 1024,
  });

  return (
    <>
      <div
        {...getRootProps()}
        className={`card p-2 text-center cursor-pointer ${isDragActive ? 'border-primary bg-primary-lt' : 'border-dashed'}`}
        style={{
          border: '2px dashed var(--tblr-border-color)',
          borderRadius: 8,
          cursor: 'pointer',
        }}
      >
        <input {...getInputProps()} />
        {preview ? (
          <div>
            <img
              src={preview}
              alt="Panel icon"
              style={{ width: 48, height: 48, objectFit: 'contain' }}
            />
            <div className="text-muted small mt-1">
              {isDragActive ? 'Drop to replace' : 'Click or drag to replace'}
            </div>
          </div>
        ) : (
          <div className="py-2">
            {isDragActive ? (
              <IconUpload className="icon text-primary mb-1" size={20} />
            ) : (
              <IconPhoto className="icon text-muted mb-1" size={20} />
            )}
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              {isDragActive ? 'Drop icon here' : 'Click or drag icon here'}
              <br />
              JPEG · PNG · SVG · max 500 KB
            </div>
          </div>
        )}
      </div>
      {dropError && <div className="text-danger small mt-1">{dropError}</div>}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

export default function StatPanelModal({ panel, onSave, onClose }: StatPanelModalProps) {
  // Pre-generate a stable ID for this modal session (used for icon upload path)
  const [panelId] = useState(() => panel?.id ?? crypto.randomUUID());

  // Display config
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [category, setCategory] = useState('');
  const [order, setOrder] = useState(0);
  const [active, setActive] = useState(true);
  const [displayType, setDisplayType] = useState<StatPanel['displayType']>('infographic');
  const [iconUrl, setIconUrl] = useState('');
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [footerField1, setFooterField1] = useState<string>('');
  const [footerField2, setFooterField2] = useState<string>('');

  // Query config
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [yearFilter, setYearFilter] = useState('');
  const [labelField, setLabelField] = useState<string>('');
  const [excludedLabelValues, setExcludedLabelValues] = useState<string[]>([]);
  const [seriesField, setSeriesField] = useState<string>('');
  const [columns, setColumns] = useState<Array<keyof StatDataPoint>>([]);

  // Available fields from Firestore (for dropdowns)
  const [availableFields, setAvailableFields] = useState<Record<string, string[]>>({});

  // Preview state
  const [previewDataPoints, setPreviewDataPoints] = useState<StatDataPoint[]>([]);
  const [previewPanel, setPreviewPanel] = useState<PanelWithData | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Save state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load available field values on mount
  useEffect(() => {
    listDataPointFields()
      .then(setAvailableFields)
      .catch(err => console.error('Failed to load data point fields:', err));
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (panel) {
      setTitle(panel.title ?? '');
      setSubtitle(panel.subtitle ?? '');
      setCategory(panel.category ?? '');
      setOrder(panel.order ?? 0);
      setActive(panel.active ?? true);
      setDisplayType(panel.displayType ?? 'infographic');
      setIconUrl(panel.iconUrl ?? '');
      setFooterField1(panel.footerField1 ?? '');
      setFooterField2(panel.footerField2 ?? '');

      const q = panel.dataQuery ?? {};
      const f = q.filters ?? {};
      const strFilters: Record<string, string> = {};
      for (const key of FILTER_KEYS) {
        strFilters[key] = (f[key] as string | undefined) ?? '';
      }
      setFilters(strFilters);
      setYearFilter(f.year !== undefined ? String(f.year) : '');
      setLabelField(q.labelField ?? '');
      setExcludedLabelValues(q.excludedLabelValues ?? []);
      setSeriesField(q.seriesField ?? '');
      setColumns((q.columns ?? []).filter(c => c !== 'value') as Array<keyof StatDataPoint>);
    }
  }, [panel]);

  // Debounced fetch of data points when category changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!category.trim()) {
      setPreviewDataPoints([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setLoadingPreview(true);
      listDataPointsByTheme(category.trim())
        .then(pts => setPreviewDataPoints(pts))
        .catch(err => console.error('Failed to load preview data:', err))
        .finally(() => setLoadingPreview(false));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [category]);

  // Recompute preview whenever panel config or data points change
  useEffect(() => {
    if (!title && !category) {
      setPreviewPanel(null);
      return;
    }

    const cleanedFilters: StatDataQuery['filters'] = {};
    for (const key of FILTER_KEYS) {
      const val = filters[key];
      if (val && val.trim()) {
        (cleanedFilters as Record<string, string>)[key] = val.trim();
      }
    }
    if (yearFilter.trim()) {
      const parsed = parseInt(yearFilter, 10);
      if (!isNaN(parsed)) cleanedFilters.year = parsed;
    }

    const builtPanel: StatPanel = {
      id: panelId,
      title: title || 'Preview',
      subtitle: subtitle || undefined,
      category,
      order,
      active,
      displayType,
      iconUrl: iconUrl || undefined,
      footerField1: (footerField1 as StatPanel['footerField1']) || undefined,
      footerField2: (footerField2 as StatPanel['footerField2']) || undefined,
      dataQuery: {
        filters: Object.keys(cleanedFilters).length > 0 ? cleanedFilters : undefined,
        labelField: (labelField as StatDataQuery['labelField']) || undefined,
        seriesField: (seriesField as StatDataQuery['seriesField']) || undefined,
        excludedLabelValues: excludedLabelValues.length > 0 ? excludedLabelValues : undefined,
        columns:
          columns.length > 0
            ? ([...columns.filter(c => c !== 'value'), 'value'] as Array<keyof StatDataPoint>)
            : undefined,
      },
    };

    setPreviewPanel(computeStatPanel(builtPanel, previewDataPoints));
  }, [
    previewDataPoints,
    title,
    subtitle,
    category,
    order,
    active,
    displayType,
    iconUrl,
    footerField1,
    footerField2,
    filters,
    yearFilter,
    labelField,
    excludedLabelValues,
    seriesField,
    columns,
    panelId,
  ]);

  function handleFilterChange(key: string, value: string) {
    setFilters(prev => ({ ...prev, [key]: value }));
  }

  function handleColumnToggle(col: keyof StatDataPoint) {
    setColumns(prev => (prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!category.trim()) {
      setError('Category is required');
      return;
    }

    const cleanedFilters: StatDataQuery['filters'] = {};
    for (const key of FILTER_KEYS) {
      const val = filters[key];
      if (val && val.trim()) {
        (cleanedFilters as Record<string, string>)[key] = val.trim();
      }
    }
    if (yearFilter.trim()) {
      const parsed = parseInt(yearFilter, 10);
      if (!isNaN(parsed)) cleanedFilters.year = parsed;
    }

    setSaving(true);
    try {
      // Upload icon if a new file was selected
      let finalIconUrl = iconUrl;
      if (iconFile) {
        finalIconUrl = await uploadStatPanelIcon(panelId, iconFile);
      }

      const data: Omit<StatPanel, 'id'> = {
        title: title.trim(),
        ...(subtitle.trim() ? { subtitle: subtitle.trim() } : {}),
        category: category.trim(),
        order,
        active,
        displayType,
        ...(finalIconUrl ? { iconUrl: finalIconUrl } : {}),
        ...(footerField1 ? { footerField1: footerField1 as StatPanel['footerField1'] } : {}),
        ...(footerField2 ? { footerField2: footerField2 as StatPanel['footerField2'] } : {}),
        dataQuery: {
          ...(Object.keys(cleanedFilters).length > 0 ? { filters: cleanedFilters } : {}),
          ...(labelField ? { labelField: labelField as StatDataQuery['labelField'] } : {}),
          ...(seriesField ? { seriesField: seriesField as StatDataQuery['seriesField'] } : {}),
          ...(excludedLabelValues.length > 0 ? { excludedLabelValues } : {}),
          ...(columns.length > 0
            ? {
                columns: [...columns.filter(c => c !== 'value'), 'value'] as Array<
                  keyof StatDataPoint
                >,
              }
            : {}),
        },
      };

      let saved: StatPanel;
      if (panel?.id) {
        await updatePanel(panel.id, data);
        saved = { ...data, id: panel.id };
      } else {
        saved = await createPanel(data, panelId);
      }
      onSave(saved);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save panel');
    } finally {
      setSaving(false);
    }
  }

  const themes = availableFields.theme ?? [];

  return (
    <div
      className="modal modal-blur fade show"
      style={{ display: 'block' }}
      tabIndex={-1}
      role="dialog"
    >
      <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{panel ? 'Edit Panel' : 'New Panel'}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              <div className="row">
                {/* Left column — form */}
                <div className="col-md-7">
                  <div className="mb-3">
                    <label className="form-label required">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Subtitle</label>
                    <input
                      type="text"
                      className="form-control"
                      value={subtitle}
                      onChange={e => setSubtitle(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label required">Category</label>
                    <select
                      className="form-select"
                      value={category}
                      onChange={e => setCategory(e.target.value)}
                      required
                    >
                      <option value="">— select —</option>
                      {themes.map(t => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    {loadingPreview && <small className="form-hint">Loading preview data…</small>}
                  </div>
                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label required">Display Type</label>
                      <select
                        className="form-select"
                        value={displayType}
                        onChange={e => setDisplayType(e.target.value as StatPanel['displayType'])}
                      >
                        {Object.entries(DISPLAY_TYPE_LABELS).map(([val, label]) => (
                          <option key={val} value={val}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label required">Order</label>
                      <input
                        type="number"
                        className="form-control"
                        value={order}
                        onChange={e => setOrder(parseInt(e.target.value, 10) || 0)}
                      />
                    </div>
                  </div>

                  {/* Active toggle */}
                  <div className="mb-3">
                    <label className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={active}
                        onChange={e => setActive(e.target.checked)}
                      />
                      <span className="form-check-label">Active (visible in mobile app)</span>
                    </label>
                  </div>

                  {/* Icon upload */}
                  <div className="mb-3">
                    <label className="form-label">Icon</label>
                    <PanelIconUpload
                      currentUrl={iconUrl}
                      onFile={(file, preview) => {
                        setIconFile(file);
                        setIconUrl(preview);
                      }}
                    />
                  </div>

                  {/* Footer fields */}
                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label">Footer Line 1</label>
                      <select
                        className="form-select form-select-sm"
                        value={footerField1}
                        onChange={e => setFooterField1(e.target.value)}
                      >
                        <option value="">— none —</option>
                        {FOOTER_FIELD_OPTIONS.map(f => (
                          <option key={f} value={f}>
                            {f}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label">Footer Line 2</label>
                      <select
                        className="form-select form-select-sm"
                        value={footerField2}
                        onChange={e => setFooterField2(e.target.value)}
                      >
                        <option value="">— none —</option>
                        {FOOTER_FIELD_OPTIONS.map(f => (
                          <option key={f} value={f}>
                            {f}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Section: Query filters */}
                  <details open className="mb-3">
                    <summary className="fw-bold mb-2" style={{ cursor: 'pointer' }}>
                      Query Filters
                    </summary>
                    <div className="mt-2">
                      {FILTER_KEYS.map(key => (
                        <div className="mb-2" key={key}>
                          <label className="form-label" style={{ textTransform: 'capitalize' }}>
                            {key}
                          </label>
                          <select
                            className="form-select form-select-sm"
                            value={filters[key] ?? ''}
                            onChange={e => handleFilterChange(key, e.target.value)}
                          >
                            <option value="">— any —</option>
                            {(availableFields[key] ?? []).map(v => (
                              <option key={v} value={v}>
                                {v}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                      <div className="mb-2">
                        <label className="form-label">Year</label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={yearFilter}
                          onChange={e => setYearFilter(e.target.value)}
                          placeholder="— any —"
                        />
                      </div>
                    </div>
                  </details>

                  {/* Section: Series config */}
                  {(displayType === 'bar_chart' ||
                    displayType === 'stacked_bar_chart' ||
                    displayType === 'pie_chart') && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">Label Field</label>
                        <select
                          className="form-select"
                          value={labelField}
                          onChange={e => {
                            setLabelField(e.target.value);
                            setExcludedLabelValues([]); // reset when field changes
                          }}
                        >
                          <option value="">— none —</option>
                          {LABEL_FIELD_OPTIONS.map(f => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                      </div>
                      {labelField &&
                        (() => {
                          const cleanedFiltersForLabels: StatDataQuery['filters'] = {};
                          for (const key of FILTER_KEYS) {
                            const val = filters[key];
                            if (val && val.trim())
                              (cleanedFiltersForLabels as Record<string, string>)[key] = val.trim();
                          }
                          if (yearFilter.trim()) {
                            const parsed = parseInt(yearFilter, 10);
                            if (!isNaN(parsed)) cleanedFiltersForLabels.year = parsed;
                          }
                          const filteredForLabels = applyFilters(
                            previewDataPoints,
                            Object.keys(cleanedFiltersForLabels).length > 0
                              ? cleanedFiltersForLabels
                              : undefined
                          );
                          const allValues = Array.from(
                            new Set(
                              filteredForLabels
                                .map(p => p[labelField as keyof typeof p])
                                .filter(v => v !== undefined && v !== null && v !== '')
                                .map(String)
                            )
                          ).sort();
                          if (allValues.length === 0) return null;
                          return (
                            <div className="mb-3">
                              <label className="form-label">
                                Label Values
                                <span
                                  className="text-muted ms-2"
                                  style={{ fontSize: '0.75rem', fontWeight: 'normal' }}
                                >
                                  uncheck to hide
                                </span>
                              </label>
                              <div
                                className="row g-1"
                                style={{ maxHeight: 160, overflowY: 'auto' }}
                              >
                                {allValues.map(v => (
                                  <div className="col-6" key={v}>
                                    <label className="form-check">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={!excludedLabelValues.includes(v)}
                                        onChange={() =>
                                          setExcludedLabelValues(prev =>
                                            prev.includes(v)
                                              ? prev.filter(x => x !== v)
                                              : [...prev, v]
                                          )
                                        }
                                      />
                                      <span className="form-check-label text-truncate" title={v}>
                                        {v}
                                      </span>
                                    </label>
                                  </div>
                                ))}
                              </div>
                              {excludedLabelValues.length > 0 && (
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0 mt-1"
                                  onClick={() => setExcludedLabelValues([])}
                                >
                                  Select all
                                </button>
                              )}
                            </div>
                          );
                        })()}
                    </>
                  )}
                  {displayType === 'stacked_bar_chart' && (
                    <div className="mb-3">
                      <label className="form-label">Series Field</label>
                      <select
                        className="form-select"
                        value={seriesField}
                        onChange={e => setSeriesField(e.target.value)}
                      >
                        <option value="">— none —</option>
                        {LABEL_FIELD_OPTIONS.map(f => (
                          <option key={f} value={f}>
                            {f}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {displayType === 'table' && (
                    <div className="mb-3">
                      <label className="form-label">Table Columns</label>
                      <small className="form-hint d-block mb-1 text-muted">
                        Group-by columns — value (sum) is always included
                      </small>
                      <div className="row g-1">
                        {TABLE_COLUMN_OPTIONS.map(col => (
                          <div className="col-6" key={col}>
                            <label className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={columns.includes(col)}
                                onChange={() => handleColumnToggle(col)}
                              />
                              <span className="form-check-label">{col}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right column — preview */}
                <div className="col-md-5">
                  {previewPanel ? (
                    <StatPanelPreviewCard panel={previewPanel} />
                  ) : (
                    <div
                      className="card h-100 d-flex align-items-center justify-content-center"
                      style={{ minHeight: 200, background: '#f8f9fa' }}
                    >
                      <div className="text-center text-muted p-4">
                        <p>Configure a panel to see a preview</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
